/*eslint sort-keys: "error"*/
import React, { Fragment } from 'react';
import { ENV_DEV } from '../../constants';
import { ENV } from '../../config';
import * as dt from '../../components/DateTime/DateTime';
import { equalsIgnoreCase } from './string';
import { neverDefault } from './neverDefault';

export const EN_US = 'en-us' as const;
export const ES_US = 'es-us' as const;
export type SupportedLocale = typeof EN_US | typeof ES_US;
export const allSupportedLocales: SupportedLocale[] = [EN_US, ES_US];

export const isSupportedLocale = (value: any): value is SupportedLocale => {
  // Pretend it's a SupportedLocale just to ensure exhaustiveness
  const val: SupportedLocale = value;
  if (val === EN_US) return true;
  if (val === ES_US) return true;
  return neverDefault(val, false);
};

export const getDapiLanguageFromLocale = (locale: SupportedLocale): 'en' | 'es' => {
  if (locale === ES_US) return 'es';
  if (locale === EN_US) return 'en';
  return neverDefault(locale, 'en');
};

const strIsToday = (dayStr: string, locale: SupportedLocale) => {
  const trimmedDayStr = dayStr?.trim() ?? '';
  if (locale === EN_US) {
    return equalsIgnoreCase(trimmedDayStr, 'today', { locale });
  }

  if (locale === ES_US) {
    return equalsIgnoreCase(trimmedDayStr, 'hoy', { locale });
  }

  return neverDefault(locale, false);
};

const strIsTomorrow = (dayStr: string, locale: SupportedLocale) => {
  const trimmedDayStr = dayStr?.trim() ?? '';
  if (locale === EN_US) {
    return equalsIgnoreCase(trimmedDayStr, 'tomorrow', { locale });
  }

  if (locale === ES_US) {
    return equalsIgnoreCase(trimmedDayStr, 'mañana', { locale });
  }

  return neverDefault(locale, false);
};

interface SimpleRenderFunc {
  (text: string): React.ReactNode;
}
interface LocalizedStrLookup {
  ACCEPT_INSURANCE_MIGHT_VARY: string;
  ADD_BACK_OF_CARD: string;
  ADD_FRONT_OF_CARD: string;
  ADD_INSURANCE: string;
  ADD_PHOTOS_OF_YOUR_INSURANCE_CARD: string;
  ADDING_INSURANCE_TO_YOUR_ACCOUNT: string;
  ALL_ONLINE_SLOTS_FULL: string;
  APT_SUITE_ETC_OPTIONAL: string;
  BACK_OF_CARD: string;
  BACK: string;
  BIRTH_DATE_MONTH: string;
  BIRTH_DATE: string;
  BIRTH_MONTH: string;
  BOOK_A_VIDEO_VISIT: string;
  BOOK_AHEAD_VISITS_FULLY_BOOKED_X_DAY(dayStr: string): string;
  BOOK_IT: string;
  BOOKING_ELLIPSIS: string;
  BOOKINGS_BY_SOLV: string;
  BUSY_TIME: string;
  CANCEL: string;
  CARDHOLDER_DETAILS: string;
  CHECKING_COVERAGE_ELLIPSIS: string;
  CHOOSE_A_TIME: string;
  CHOOSE_DATE_AND_TIME: string;
  CITY: string;
  CLINIC_CLOSED: string;
  CLOSED_FOR_LUNCH_TIMES: string;
  CLOSED: string;
  COMPLETE_YOUR_INSURANCE_DETAILS: string;
  CONNECT_WITH_PROVIDER_AT_HOME(locationName: string): string;
  CONTINUE: string;
  DAY: string;
  DECIDE_LATER: string;
  DOESNT_ACCEPT_MEDICAID: string;
  DOESNT_ACCEPT_MEDICARE_MEDICAID: string;
  DOESNT_ACCEPT_MEDICARE: string;
  DONT_KNOW: string;
  DOSE_1: string;
  DOSE_2: string;
  EMAIL_ADDRESS_OPTIONAL: string;
  EMAIL_ADDRESS: string;
  ENTER_YOUR_ADDRESS_HERE: string;
  FEWER_DATES: string;
  FEWER_TIMES: string;
  FINALIZE_YOUR_VISIT: string;
  FIND_NEXT_AVAILABLE_VISIT: string;
  FIRST_NAME: string;
  FLIP_CARD_OVER_AND: string;
  FOR_EMERGENCIES_CALL_911(render911Link: SimpleRenderFunc): React.ReactNode;
  FRONT_OF_CARD: string;
  HAVE_CARD_PHOTO_FRONT: string;
  HAVE_YOU_BEEN_BEFORE(locationName: string): string;
  HIGH_DEMAND_ALL_APPOINTMENTS_BOOKED: string;
  HOME_ADDRESS: string;
  HOUR_TO_HOUR(startTime: string, endTime: string): string;
  HOW_DO_YOU_WANT_TO_PAY_ASTERISK: string;
  HOW_DO_YOU_WANT_TO_PAY: string;
  I_GIVE_TELEMED_CONSENT(renderTermsLink: SimpleRenderFunc): React.ReactNode;
  I_HAVE_READ_UNDERSTAND_TOS_PP(
    renderTOS: SimpleRenderFunc,
    renderPP: SimpleRenderFunc
  ): React.ReactNode;
  IM_NOT_USING_INSURANCE: string;
  IN_PERSON_VISIT: string;
  INSURANCE_CARD_PHOTOS_SAVED: string;
  INSURANCE_CARRIER: string;
  INSURANCE_TYPE: string;
  INSURANCE: string;
  LAST_NAME: string;
  LEGAL_FIRST_NAME: string;
  LEGAL_LAST_NAME: string;
  LOADING: string;
  LOG_IN_TO_YOUR_SOLV_ACCOUNT(renderLoginLink: SimpleRenderFunc): React.ReactNode;
  LOG_IN: string;
  MEMBER_ID: string;
  MOBILE_NUMBER: string;
  MONTH: string;
  MORE_DATES: string;
  MORE_TIMES: string;
  NO_AVAILABILITY_X_DAY(dayStr: string): string;
  NO_VISIT_TIMES_LEFT: string;
  NO: string;
  ONLINE_BOOKING_ONLY_TODAY_OR_TOMORROW: string;
  OPEN_24_HOURS: string;
  OPEN_NOW: string;
  OPEN_WALK_IN_ONLY: string;
  OPENING_SOON: string;
  OTHER: string;
  PATIENT_FIRST_NAME: string;
  PATIENT_INFO: string;
  PATIENT_LAST_NAME: string;
  PATIENT_SEX: string;
  PICKUP_ADDRESS: string;
  PLACE_ON_FLAT_SURFACE: string;
  PRIVACY_POLICY: string;
  REASON_FOR_VIDEO_VISIT: string;
  REASON_FOR_VISIT: string;
  REQUIRED: string;
  SAVE_INSURANCE: string;
  SCANNING_AND_SAVING_PHOTOS: string;
  SCANNING_YOUR_INSURANCE: string;
  SELECT_A_NEW_TIME: string;
  SELECT: string;
  SELECT_A_REASON_FOR_VISIT: string;
  SELF_PAY_IS_FLAT_RATE_OF(priceString: string): string;
  SELF_PAY: string;
  SENDING_YOUR_INSURANCE_TO_THE_CLINIC: string;
  SEX_FEMALE: string;
  SEX_MALE: string;
  SEX_OTHER: string;
  SHOW_MORE: string;
  SKIP_UPLOADING_PHOTOS: string;
  SKIP_ADDING_INSURANCE: string;
  SORRY_SLOT_NO_LONGER_AVAILABLE_COVID: string;
  STATE: string;
  STREET_ADDRESS: string;
  TAKE_A_PHOTO: string;
  TERMS_OF_SERVICE: string;
  THIS_CLINIC_IS_CLOSED_X_DAY(dayStr: string): string;
  THIS_LANGUAGE: string;
  TODAY: string;
  TOMORROW: string;
  UPDATE_INSURANCE: string;
  UPLOAD_IMAGE: string;
  VIDEO_VISIT: string;
  VISIT_TIME_DISCLAIMER(locationName: string): string;
  WALK_IN_ONLY: string;
  WHAT_FOR_SYMPTOM: string;
  WHAT_TYPE_VISIT_DO_YOU_WANT: string;
  WHICH_DOSE_YOU_SCHEDULING: string;
  WILL_SEND_INSURANCE_TO_CLINIC: string;
  WITHIN_X_MINUTES(numberMin: string | number): string;
  WOULD_YOU_LIKE_FREE_UBER_RIDE: string;
  YEAR: string;
  YES: string;
  YESTERDAY: string;
  YOU_CAN_STILL_BOOK_FOR_TOMORROW: string;
  YOU_CAN_STILL_BOOK_X_DAY(dayStr: string): string;
  YOU_CAN_STILL_WALK_IN_OR_BOOK_TOMORROW: string;
  YOU_CAN_STILL_WALK_IN: string;
  YOU_CAN_USE_OTHER_INSURANCE: string;
  ZIP_CODE: string;

  /** DateTime format strings for formatting via moment etc. */
  dtFormat: {
    /** "March 3rd" */
    STD_MONTH_AND_DATE: string;
    /** "10:30 am" */
    STD_HOUR_AND_PERIOD: string;
    /** "March 27th at 10:30 am" */
    STD_MONTH_AND_DATE_AT_TIME_AND_PERIOD: string;
    /** "Select March 27th at 10:30 am" */
    SELECT_DATE_AT_TIME: string;
    /** "Tuesday, March 30th" */
    DAY_OF_WEEK_DATE_OF_MONTH: string;
    /** "Last Friday" */
    LAST_DAY_OF_WEEK: string;
  };
}

const localizedStr: {
  [locale in SupportedLocale]: LocalizedStrLookup;
} = {
  [EN_US]: {
    ACCEPT_INSURANCE_MIGHT_VARY:
      'If you select insurance, your responsibility will vary depending on your plan.',
    ADDING_INSURANCE_TO_YOUR_ACCOUNT: 'Adding insurance to your account...',
    ADD_BACK_OF_CARD: 'Add back of card',
    ADD_FRONT_OF_CARD: 'Add front of card',
    ADD_INSURANCE: 'add insurance',
    ADD_PHOTOS_OF_YOUR_INSURANCE_CARD: 'Add photos of your insurance card',
    ALL_ONLINE_SLOTS_FULL: 'All online booking slots are full.',
    APT_SUITE_ETC_OPTIONAL: 'Apt, Suite, etc. (optional)',
    BACK: 'back',
    BACK_OF_CARD: 'Back of card',
    BIRTH_DATE: 'birth date',
    BIRTH_DATE_MONTH: 'Birth date: Month',
    BIRTH_MONTH: 'birth month',
    BOOKINGS_BY_SOLV: 'Bookings by Solv.',
    BOOKING_ELLIPSIS: 'booking...',
    BOOK_AHEAD_VISITS_FULLY_BOOKED_X_DAY(dayStr: string) {
      return `Book ahead visits are fully booked ${dayStr}.`;
    },
    BOOK_A_VIDEO_VISIT: 'Book a Video Visit',
    BOOK_IT: 'book it',
    BUSY_TIME: 'busy time',
    CANCEL: 'cancel',
    CARDHOLDER_DETAILS: 'cardholder details:',
    CHECKING_COVERAGE_ELLIPSIS: 'checking coverage...',
    CHOOSE_A_TIME: 'Choose a Time',
    CHOOSE_DATE_AND_TIME: 'choose a date and time',
    CITY: 'city',
    CLINIC_CLOSED: 'clinic closed',
    CLOSED: 'closed',
    CLOSED_FOR_LUNCH_TIMES: 'Closed for lunch daily from 1pm to 2pm',
    COMPLETE_YOUR_INSURANCE_DETAILS: 'Complete your insurance details',
    CONNECT_WITH_PROVIDER_AT_HOME(providerName) {
      return `Connect with a provider at ${providerName} in the comfort of home.`;
    },
    CONTINUE: 'continue',
    DAY: 'day',
    DECIDE_LATER: 'decide later',
    DOESNT_ACCEPT_MEDICAID: "This clinic doesn't accept Medicaid.",
    DOESNT_ACCEPT_MEDICARE: "This clinic doesn't accept Medicare.",
    DOESNT_ACCEPT_MEDICARE_MEDICAID: "This clinic doesn't accept Medicare or Medicaid.",
    DONT_KNOW: "don't know",
    DOSE_1: 'dose 1',
    DOSE_2: 'dose 2',
    EMAIL_ADDRESS: 'email address',
    EMAIL_ADDRESS_OPTIONAL: 'email address (optional)',
    ENTER_YOUR_ADDRESS_HERE: 'Enter your address here',
    FEWER_DATES: 'fewer dates',
    FEWER_TIMES: 'Fewer times',
    FINALIZE_YOUR_VISIT: 'finalize your visit',
    FIND_NEXT_AVAILABLE_VISIT: 'find next available visit',
    FIRST_NAME: 'first name',
    FLIP_CARD_OVER_AND: 'Flip card over and tap button below to capture the back of card.',
    FOR_EMERGENCIES_CALL_911(render911Link) {
      return <>For emergencies {render911Link('please call 911')}</>;
    },
    FRONT_OF_CARD: 'Front of card',
    HAVE_CARD_PHOTO_FRONT: 'Have a photo of the front of your card?',
    HAVE_YOU_BEEN_BEFORE(locationName) {
      return `Have you been to ${locationName} before?`;
    },
    HIGH_DEMAND_ALL_APPOINTMENTS_BOOKED:
      'Due to high demand all of our appointments are booked for today. Please check back tomorrow.',
    HOME_ADDRESS: 'home address',
    HOUR_TO_HOUR(startTime: string, endTime: string) {
      return `${startTime} to ${endTime}`;
    },
    HOW_DO_YOU_WANT_TO_PAY: 'How do you want to pay?',
    HOW_DO_YOU_WANT_TO_PAY_ASTERISK: 'How do you want to pay?*',
    IM_NOT_USING_INSURANCE: "I'm not using insurance",
    INSURANCE: 'insurance',
    INSURANCE_CARD_PHOTOS_SAVED: 'Insurance card photos saved',
    INSURANCE_CARRIER: 'insurance carrier',
    INSURANCE_TYPE: 'insurance type',
    IN_PERSON_VISIT: 'In-Person Visit',
    I_GIVE_TELEMED_CONSENT(renderTermsLink) {
      return (
        <>
          I give my consent for the use of telemedicine in my medical care and have read,
          understand, and agree to these {renderTermsLink('Terms')}
        </>
      );
    },
    I_HAVE_READ_UNDERSTAND_TOS_PP(renderTOS, renderPP) {
      return (
        <>
          I have read, understand and agree to Solv Health’s {renderTOS('Terms of Service')}, and{' '}
          {renderPP('Privacy Policy')}.
        </>
      );
    },
    LAST_NAME: 'last name',
    LEGAL_FIRST_NAME: 'legal first name',
    LEGAL_LAST_NAME: 'legal last name',
    LOADING: 'loading',
    LOG_IN: 'log in',
    LOG_IN_TO_YOUR_SOLV_ACCOUNT(renderLoginLink) {
      return <>{renderLoginLink('Log in')} to your Solv account.</>;
    },
    MEMBER_ID: 'Member ID',
    MOBILE_NUMBER: 'mobile number',
    MONTH: 'month',
    MORE_DATES: 'more dates',
    MORE_TIMES: 'More times',
    NO: 'no',
    NO_AVAILABILITY_X_DAY(dayStr) {
      return `No availability ${dayStr}`;
    },
    NO_VISIT_TIMES_LEFT: 'no visit times left',
    ONLINE_BOOKING_ONLY_TODAY_OR_TOMORROW:
      'Online booking is only available for same day and next day. Please check back tomorrow.',
    OPENING_SOON: 'opening soon',
    OPEN_24_HOURS: 'open 24 hours',
    OPEN_NOW: 'open now',
    OPEN_WALK_IN_ONLY: 'Open now - Walk in only',
    OTHER: 'other',
    PATIENT_FIRST_NAME: 'patient first name',
    PATIENT_INFO: 'patient info',
    PATIENT_LAST_NAME: 'patient last name',
    PATIENT_SEX: 'patient sex',
    PICKUP_ADDRESS: 'pickup address',
    PLACE_ON_FLAT_SURFACE: 'Place card on a flat, well lit surface without glare, and tap below.',
    PRIVACY_POLICY: 'Privacy Policy',
    REASON_FOR_VIDEO_VISIT: 'reason for video visit',
    REASON_FOR_VISIT: 'reason for visit',
    REQUIRED: 'required',
    SAVE_INSURANCE: 'save insurance',
    SCANNING_AND_SAVING_PHOTOS: 'Scanning and saving photos...',
    SCANNING_YOUR_INSURANCE: 'Scanning your insurance',
    SELECT: 'select',
    SELECT_A_NEW_TIME: 'Select a new time.',
    SELECT_A_REASON_FOR_VISIT: 'Select a reason for visit',
    SELF_PAY: 'self-pay',
    SELF_PAY_IS_FLAT_RATE_OF(priceString: string) {
      return `Self-pay is a flat rate of ${priceString}.`;
    },
    SENDING_YOUR_INSURANCE_TO_THE_CLINIC: 'Sending your insurance to the clinic...',
    SEX_FEMALE: 'female',
    SEX_MALE: 'male',
    SEX_OTHER: 'other',
    SHOW_MORE: 'show more',
    SKIP_ADDING_INSURANCE: 'skip adding insurance',
    SKIP_UPLOADING_PHOTOS: 'skip uploading photos',
    SORRY_SLOT_NO_LONGER_AVAILABLE_COVID:
      'Sorry, the time you selected is no longer available. We’re experiencing increased demand due to covid.',
    STATE: 'state',
    STREET_ADDRESS: 'street address',
    TAKE_A_PHOTO: 'take a photo',
    TERMS_OF_SERVICE: 'Terms of Service',
    THIS_CLINIC_IS_CLOSED_X_DAY(dayStr) {
      return `This clinic is closed ${dayStr}.`;
    },
    THIS_LANGUAGE: 'english',
    TODAY: 'today',
    TOMORROW: 'tomorrow',
    UPDATE_INSURANCE: 'update insurance',
    UPLOAD_IMAGE: 'upload image',
    VIDEO_VISIT: 'Video Visit',
    VISIT_TIME_DISCLAIMER(locationName) {
      return `This is your estimated time. Delays may arise if ${locationName} is unexpectedly busy.`;
    },
    WALK_IN_ONLY: 'walk in only',
    WHAT_FOR_SYMPTOM: 'What for? (fever, sore throat, sprain, etc.)',
    WHAT_TYPE_VISIT_DO_YOU_WANT: 'What type of visit would you like to book?',
    WHICH_DOSE_YOU_SCHEDULING: 'Which dose are you scheduling?',
    WILL_SEND_INSURANCE_TO_CLINIC: "We'll send your insurance to the clinic to verify",
    WITHIN_X_MINUTES(numberMin: number) {
      return numberMin === 1 ? `Within ${numberMin} minute` : `Within ${numberMin} minutes`;
    },
    WOULD_YOU_LIKE_FREE_UBER_RIDE: 'Would you like a free uber ride to the clinic?',
    YEAR: 'year',
    YES: 'yes',
    YESTERDAY: 'yesterday',
    YOU_CAN_STILL_BOOK_FOR_TOMORROW: 'You can still walk book for a visit tomorrow.',
    YOU_CAN_STILL_BOOK_X_DAY(dayStr) {
      return `You can still book a visit for ${dayStr}.`;
    },
    YOU_CAN_STILL_WALK_IN: 'You can still walk in to the clinic.',
    YOU_CAN_STILL_WALK_IN_OR_BOOK_TOMORROW:
      'You can still walk in to the clinic or book for a visit tomorrow.',
    YOU_CAN_USE_OTHER_INSURANCE: 'You can still book with this clinic and use another insurance.',
    ZIP_CODE: 'ZIP code',

    // eslint-disable-next-line sort-keys
    dtFormat: {
      /** Tuesday, March 30 */
      DAY_OF_WEEK_DATE_OF_MONTH: dt.DAY_FULL_MONTH_DAY_NUMBER,
      /** "Last Friday" */
      LAST_DAY_OF_WEEK: `[Last] ${dt.dayFull}`,
      /** Select March 27th at 10:30 am */
      SELECT_DATE_AT_TIME: `[Select] ${dt.MONTH_DAY_ORD_AT_HOUR_MINUTE_PERIOD}`,
      /** 1:30 pm */
      STD_HOUR_AND_PERIOD: dt.HOUR_MINUTE_PERIOD,
      /** March 3rd */
      STD_MONTH_AND_DATE: dt.MONTH_DAY_ORDINAL,
      /** March 27th at 10:30 am */
      STD_MONTH_AND_DATE_AT_TIME_AND_PERIOD: dt.MONTH_DAY_ORD_AT_HOUR_MINUTE_PERIOD,
    },
  },

  [ES_US]: {
    ACCEPT_INSURANCE_MIGHT_VARY: 'Si eliges seguro, tu responsabilidad variará según tu plan.',
    ADDING_INSURANCE_TO_YOUR_ACCOUNT: 'Agregar seguro a su cuenta...',
    ADD_BACK_OF_CARD: 'Agregar el reverso de la tarjeta',
    ADD_FRONT_OF_CARD: 'Agregar el frente de la tarjeta',
    ADD_INSURANCE: 'agregar seguro',
    ADD_PHOTOS_OF_YOUR_INSURANCE_CARD: 'Agrega fotos de tu tarjeta de seguro',
    ALL_ONLINE_SLOTS_FULL: 'Todas las citas en línea están agotadas.',
    APT_SUITE_ETC_OPTIONAL: 'Apt. Suite, etc. (opcional)',
    BACK: 'regresar',
    BACK_OF_CARD: 'Reverso de la tarjeta',
    BIRTH_DATE: 'fecha de nacimiento',
    BIRTH_DATE_MONTH: 'fecha de nacimiento: mes',
    BIRTH_MONTH: 'mes de nacimiento',
    BOOKINGS_BY_SOLV: 'Reservaciones por Solv.',
    BOOKING_ELLIPSIS: 'agendando…',
    BOOK_AHEAD_VISITS_FULLY_BOOKED_X_DAY(dayStr: string) {
      if (strIsToday(dayStr, ES_US) || strIsTomorrow(dayStr, ES_US)) {
        return `Las visitas con cita están completamente agendadas para ${dayStr}.`;
      }
      return `Las visitas con cita están completamente agendadas le ${dayStr}.`;
    },
    BOOK_A_VIDEO_VISIT: 'Agenda una cita de video',
    BOOK_IT: 'reservarlo',
    BUSY_TIME: 'horario concurrido',
    CANCEL: 'cancelar',
    CARDHOLDER_DETAILS: 'datos del titular:',
    CHECKING_COVERAGE_ELLIPSIS: 'verificando póliza...',
    CHOOSE_A_TIME: 'Elegir una Hora',
    CHOOSE_DATE_AND_TIME: 'elija una fecha y hora',
    CITY: 'ciudad',
    CLINIC_CLOSED: 'clínica cerrada',
    CLOSED: 'cerrado',
    CLOSED_FOR_LUNCH_TIMES: 'Cerrado a la hora del almuerzo todos los días de 1 pm a 2 pm.',
    COMPLETE_YOUR_INSURANCE_DETAILS: 'Completa los detalles de tu seguro',
    CONNECT_WITH_PROVIDER_AT_HOME(providerName) {
      return `Conéctate con un proveedor en ${providerName} en la comodidad de tu hogar.`;
    },
    CONTINUE: 'continuar',
    DAY: 'día',
    DECIDE_LATER: 'decidir después',
    DOESNT_ACCEPT_MEDICAID: 'Esta clínica no acepta Medicaid.',
    DOESNT_ACCEPT_MEDICARE: 'Esta clínica no acepta Medicare.',
    DOESNT_ACCEPT_MEDICARE_MEDICAID: 'Esta clínica no acepta Medicare ni Medicaid.',
    DONT_KNOW: 'no se',
    DOSE_1: 'dosis 1',
    DOSE_2: 'dosis 2',
    EMAIL_ADDRESS: 'dirección de email',
    EMAIL_ADDRESS_OPTIONAL: 'dirección de email (opcional)',
    ENTER_YOUR_ADDRESS_HERE: 'Ingrese su dirección aquí',
    FEWER_DATES: 'menos fechas',
    FEWER_TIMES: 'Menos veces',
    FINALIZE_YOUR_VISIT: 'finalizar su visita',
    FIND_NEXT_AVAILABLE_VISIT: 'encuentre la siguiente visita disponible',
    FIRST_NAME: 'Nombre(s)',
    FLIP_CARD_OVER_AND: 'Flip card over and tap button below to capture the back of card.',
    FOR_EMERGENCIES_CALL_911(render911Link) {
      return <>Para emergencias {render911Link('llame el 911')}</>;
    },
    FRONT_OF_CARD: 'Anverso de la tarjeta',
    HAVE_CARD_PHOTO_FRONT: '¿Tiene una foto del anverso de su tarjeta?',
    HAVE_YOU_BEEN_BEFORE(locationName) {
      return `¿Ha estado en ${locationName} antes?`;
    },
    HIGH_DEMAND_ALL_APPOINTMENTS_BOOKED:
      'Debido a la gran demanda, todas nuestras citas de hoy están agotadas. Vuelve a intentar mañana.',
    HOME_ADDRESS: 'dirección residencial',
    HOUR_TO_HOUR(startTime: string, endTime: string) {
      return `${startTime} a ${endTime}`;
    },
    HOW_DO_YOU_WANT_TO_PAY: '¿cómo desea pagar?',
    HOW_DO_YOU_WANT_TO_PAY_ASTERISK: '¿cómo desea pagar?*',
    IM_NOT_USING_INSURANCE: "I'm not using insurance", // TODO: translate
    INSURANCE: 'seguro',
    INSURANCE_CARD_PHOTOS_SAVED: 'Fotos de la tarjeta del seguro guardadas',
    INSURANCE_CARRIER: 'aseguradora',
    INSURANCE_TYPE: 'tipo de seguro',
    IN_PERSON_VISIT: 'Visita en persona',
    I_GIVE_TELEMED_CONSENT(renderTermsLink) {
      return (
        <>
          Doy mi consentimiento para el uso de telemedicina en mi atención médica y he leído,
          entendido y acepto estos {renderTermsLink('Términos')}
        </>
      );
    },
    I_HAVE_READ_UNDERSTAND_TOS_PP(renderTOS, renderPP) {
      return (
        <>
          He leído, entiendo y acepto los {renderTOS('Términos de Servicio')} y la{' '}
          {renderPP('Política de Privacidad')} de Solv Health.
        </>
      );
    },
    LAST_NAME: 'Apellido(s)',
    LEGAL_FIRST_NAME: 'Nombre(s) legal(s)',
    LEGAL_LAST_NAME: 'Apellido(s) legal(s)',
    LOADING: 'cargando',
    LOG_IN: 'iniciar sesión',
    LOG_IN_TO_YOUR_SOLV_ACCOUNT(renderLoginLink) {
      return <>{renderLoginLink('Inicia sesión')} en tu cuenta Solv</>;
    },
    MEMBER_ID: 'ID de Miembro',
    MOBILE_NUMBER: 'número de celular',
    MONTH: 'mes',
    MORE_DATES: 'más fechas',
    MORE_TIMES: 'Más horas',
    NO: 'no',
    NO_AVAILABILITY_X_DAY(dayStr) {
      if (strIsToday(dayStr, ES_US) || strIsTomorrow(dayStr, ES_US)) {
        return `No hay disponibilidad para ${dayStr}`;
      }
      return `No hay disponibilidad el ${dayStr}`;
    },
    NO_VISIT_TIMES_LEFT: 'No quedan horarios de visita',
    ONLINE_BOOKING_ONLY_TODAY_OR_TOMORROW:
      'Agendar en línea únicamente está disponible para el mismo día y el día siguiente. Vuelve a intentar mañana.',
    OPENING_SOON: 'abriendo pronto',
    OPEN_24_HOURS: 'abierto las 24 horas',
    OPEN_NOW: 'actualmente abierto',
    OPEN_WALK_IN_ONLY: 'Abierto ahora – Sin cita',
    OTHER: 'otro',
    PATIENT_FIRST_NAME: 'Nombre(s) del paciente',
    PATIENT_INFO: 'información del paciente',
    PATIENT_LAST_NAME: 'Apellido(s) del paciente',
    PATIENT_SEX: 'sexo del paciente',
    PICKUP_ADDRESS: 'dirección de recogida',
    PLACE_ON_FLAT_SURFACE:
      'Coloca la tarjeta en una superficie plana y bien iluminada (sin reflejos) y toca el botón debajo.',
    PRIVACY_POLICY: 'Política de Privacidad',
    REASON_FOR_VIDEO_VISIT: 'motivo de la cita por video',
    REASON_FOR_VISIT: 'motivo de visita',
    REQUIRED: 'requerido',
    SAVE_INSURANCE: 'guardar seguro',
    SCANNING_AND_SAVING_PHOTOS: 'Escaneando y guardando las fotografías...',
    SCANNING_YOUR_INSURANCE: 'Escaneando tu seguro',
    SELECT: 'seleccione',
    SELECT_A_NEW_TIME: 'Seleccione una nueva hora.',
    SELECT_A_REASON_FOR_VISIT: 'Seleccione un motivo de visita',
    SELF_PAY: 'auto-pago',
    SELF_PAY_IS_FLAT_RATE_OF(priceString: string) {
      return `El pago por cuenta propia tiene una tarifa fija de ${priceString}.`;
    },
    SENDING_YOUR_INSURANCE_TO_THE_CLINIC: 'Enviando su seguro a la clínica...',
    SEX_FEMALE: 'femenino',
    SEX_MALE: 'masculino',
    SEX_OTHER: 'otro',
    SHOW_MORE: 'mostrar más',
    SKIP_ADDING_INSURANCE: 'omitir agregar seguro',
    SKIP_UPLOADING_PHOTOS: 'omitir subir fotos',
    SORRY_SLOT_NO_LONGER_AVAILABLE_COVID:
      'Lo sentimos, la hora que seleccionó ya no está disponible. Estamos experimentando una mayor demanda debido al covid.',
    STATE: 'estado',
    STREET_ADDRESS: 'dirección postal/física',
    TAKE_A_PHOTO: 'tomar una foto',
    TERMS_OF_SERVICE: 'Términos de Servicio',
    THIS_CLINIC_IS_CLOSED_X_DAY(dayStr) {
      return `Esta clínica está cerrada ${dayStr}.`;
    },
    THIS_LANGUAGE: 'español',
    TODAY: 'hoy',
    TOMORROW: 'mañana',
    UPDATE_INSURANCE: 'actualizar el seguro',
    UPLOAD_IMAGE: 'cargar imagen',
    VIDEO_VISIT: 'Visita en video',
    VISIT_TIME_DISCLAIMER(locationName) {
      return `This is your estimated time. Delays may arise if ${locationName} is unexpectedly busy.`; // TODO: translate
    },
    WALK_IN_ONLY: 'sin cita',
    WHAT_FOR_SYMPTOM: '¿Debido a que? (fiebre, dolor de garganta, torcedura, etc.)',
    WHAT_TYPE_VISIT_DO_YOU_WANT: '¿Qué tipo de visita te gustaría agendar?',
    WHICH_DOSE_YOU_SCHEDULING: '¿Qué dosis está programando?',
    WILL_SEND_INSURANCE_TO_CLINIC: 'Enviaremos tu seguro a la clínica para verificar',
    WITHIN_X_MINUTES(numberMin: number) {
      return numberMin === 1 ? `Dentro de ${numberMin} minuto` : `Dentro de ${numberMin} minutos`;
    },
    WOULD_YOU_LIKE_FREE_UBER_RIDE: '¿Te gustaría un viaje gratis en Uber a la clínica?',
    YEAR: 'año',
    YES: 'sí',
    YESTERDAY: 'ayer',
    YOU_CAN_STILL_BOOK_FOR_TOMORROW: 'Aún puedes agendar una visita mañana.',
    YOU_CAN_STILL_BOOK_X_DAY(dayStr) {
      return `Aún puedes reservar una cita para ${dayStr}`;
    },
    YOU_CAN_STILL_WALK_IN: 'Aún puedes acudir sin cita a la clínica.',
    YOU_CAN_STILL_WALK_IN_OR_BOOK_TOMORROW:
      'Aún puedes acudir sin cita a la clínica o agendar una visita para mañana.',
    YOU_CAN_USE_OTHER_INSURANCE: 'Aún puedes agendar con esta clínica y utilizar otro seguro.',
    ZIP_CODE: 'Código postal',

    // eslint-disable-next-line sort-keys
    dtFormat: {
      /** viernes, 3 de marzo */
      DAY_OF_WEEK_DATE_OF_MONTH: dt.ES_DAY_FULL_DAY_NUMBER_MONTH,
      /** El viernes pasado */
      LAST_DAY_OF_WEEK: `[El] ${dt.dayFull} [pasado]`,
      /** Seleccione 27 de marzo a las 10:00 am */
      SELECT_DATE_AT_TIME: `[Seleccione] ${dt.ES_DAY_NUM_OF_MONTH_AT_HOUR_MINUTE_PERIOD}`,
      /** 10:30 am */
      STD_HOUR_AND_PERIOD: dt.HOUR_MINUTE_PERIOD,
      /** 3 de marzo */
      STD_MONTH_AND_DATE: dt.ES_DAY_NUM_OF_MONTH,
      /** 27 de marzo a las 10:30 am */
      STD_MONTH_AND_DATE_AT_TIME_AND_PERIOD: dt.ES_DAY_NUM_OF_MONTH_AT_HOUR_MINUTE_PERIOD,
    },
  },
};

export const enUsStrings = localizedStr[EN_US];
export const esUsStrings = localizedStr[ES_US];

export default localizedStr;
